import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Department, Gender, Track } from "../data/sharedTypes";
import { getCollegeNameKey } from "../data/sharedTypes";
import type { UniversityId } from "../data/universityRegistry";
import { getDepartmentThreshold, isFlatStructure } from "../data/universityRegistry";
import {
    type Scores,
    type QualifiedCollegeWithDepts,
    getQualifiedCollegesWithDepartments,
} from "../utils/calculateEquivalentRate";

interface Props {
    universityId: UniversityId;
    scores: Scores;
    selectedTrack?: Track | null;
    selectedGender?: Gender | null;
    selectedCollegeKey?: string | null;
    selectedDepartment?: Department | null;
    onClose: () => void;
}

export default function QualifiedCollegesModal({ universityId, scores, selectedTrack, selectedGender, selectedCollegeKey, selectedDepartment, onClose }: Props) {
    const { t } = useTranslation();
    const allQualified: QualifiedCollegeWithDepts[] =
        getQualifiedCollegesWithDepartments(universityId, scores, selectedGender ?? undefined, selectedTrack);
    const isFlat = isFlatStructure(universityId);

    // For Kuwait University, filter by selected track (scientific/literary)
    // Education colleges are track-specific; all others appear in both
    const trackScoped = useMemo(() => {
        if (!selectedTrack || universityId !== "kuwait_university") return allQualified;
        return allQualified.filter((item) => {
            if (item.college.key === "education_scientific") return selectedTrack === "scientific";
            if (item.college.key === "education_literary") return selectedTrack === "literary";
            return true;
        });
    }, [allQualified, selectedTrack, universityId]);

    // Scope strictly to the user's selected college (no fallback to other colleges).
    // For Kuwait University Education, the scientific/literary buckets are sibling
    // colleges in the data — treat them as one "Education" category when scoping.
    // Then drop the user's already-selected department from each remaining bucket,
    // because "opportunities" means *other* specialties they qualify for, not the one
    // they just calculated. Colleges left with no departments after the drop disappear.
    const qualified = useMemo(() => {
        const collegeScoped = (() => {
            if (!selectedCollegeKey) return trackScoped;
            const educationKeys = new Set(["education_scientific", "education_literary"]);
            const isKuEducation = universityId === "kuwait_university" && educationKeys.has(selectedCollegeKey);
            return trackScoped.filter((item) =>
                isKuEducation
                    ? educationKeys.has(item.college.key)
                    : item.college.key === selectedCollegeKey,
            );
        })();

        if (!selectedDepartment) return collegeScoped;
        return collegeScoped
            .map((item) => ({
                ...item,
                departments: item.departments.filter(
                    ({ department }) => department.code !== selectedDepartment.code,
                ),
            }))
            .filter((item) => item.departments.length > 0);
    }, [trackScoped, selectedCollegeKey, selectedDepartment, universityId]);

    const [searchQuery, setSearchQuery] = useState("");

    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return qualified;

        return qualified
            .map((item) => {
                const collegeName = t(getCollegeNameKey(item.college)).toLowerCase();
                if (collegeName.includes(q)) return item;

                const matchingDepts = item.departments.filter(({ department }) => {
                    const deptName = department.departmentKey
                        ? (universityId === "internal_missions"
                              ? t(`dream_university_institution_${department.departmentKey}`)
                              : isFlat
                                ? t(`dream_university_country_${department.departmentKey}`)
                                : t(`dream_university_dept_${department.departmentKey}`)).toLowerCase()
                        : collegeName;
                    const trackName = department.track
                        ? t(`dream_university_track_${department.track}`).toLowerCase()
                        : "";
                    return deptName.includes(q) || trackName.includes(q);
                });

                if (matchingDepts.length === 0) return null;
                return { ...item, departments: matchingDepts };
            })
            .filter(Boolean) as QualifiedCollegeWithDepts[];
    }, [qualified, searchQuery, t]);

    const totalDepts = filtered.reduce(
        (sum, item) => sum + item.departments.length,
        0,
    );

    const [expandedColleges, setExpandedColleges] = useState<
        Record<string, boolean>
    >(() => {
        const initial: Record<string, boolean> = {};
        for (const item of qualified) {
            initial[item.college.key] = true;
        }
        return initial;
    });

    const toggleCollege = (key: string) => {
        setExpandedColleges((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    /**
     * For Foreign/Internal Missions, departments are countries.
     * Use a different label key for the department name.
     */
    const getDeptDisplayName = (department: { collegeKey: string; departmentKey: string | null }) => {
        if (universityId === "internal_missions" && department.departmentKey) {
            return t(`dream_university_institution_${department.departmentKey}`);
        }
        if (isFlat && department.departmentKey) {
            return t(`dream_university_country_${department.departmentKey}`);
        }
        if (department.departmentKey) {
            return t(`dream_university_dept_${department.departmentKey}`);
        }
        return t(getCollegeNameKey({ key: department.collegeKey }));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg lg:max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900">
                        {totalDepts} {t("dream_university_opportunities")}
                    </h3>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Search */}
                <div className="px-5 py-3 border-b border-gray-100">
                    <div className="relative">
                        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t("dream_university_search_sections")}
                            className="w-full ps-9 pe-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent placeholder:text-gray-400"
                        />
                    </div>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1">
                    {filtered.map((item) => {
                        const isExpanded =
                            expandedColleges[item.college.key] ?? true;

                        return (
                            <div
                                key={item.college.key}
                                className="border-b border-gray-100 last:border-b-0"
                            >
                                {/* College Header */}
                                <button
                                    onClick={() =>
                                        toggleCollege(item.college.key)
                                    }
                                    className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">
                                            {item.college.emoji}
                                        </span>
                                        <span className="text-sm font-bold text-gray-900">
                                            {t(getCollegeNameKey(item.college))}
                                        </span>
                                        <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full">
                                            {item.departments.length}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-theme-700">
                                            {item.studentRate}
                                        </span>
                                        {isExpanded ? (
                                            <ChevronUp className="w-4 h-4 text-gray-400" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                        )}
                                    </div>
                                </button>

                                {/* Department rows */}
                                {isExpanded && (
                                    <div>
                                        {/* Column headers */}
                                        <div className="grid grid-cols-3 gap-2 px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                                            <div>
                                                {isFlat
                                                    ? t("dream_university_country")
                                                    : t("dream_university_section")}
                                            </div>
                                            <div className="text-center">
                                                {t(
                                                    "dream_university_last_year_min",
                                                )}
                                            </div>
                                            <div className="text-end">
                                                {t(
                                                    "dream_university_your_rate",
                                                )}
                                            </div>
                                        </div>
                                        {item.departments.map(
                                            ({ department }, idx) => (
                                                <div
                                                    key={`${department.code}-${department.departmentKey}-${department.track ?? ""}-${idx}`}
                                                    className="grid grid-cols-3 gap-2 px-5 py-2.5 items-center hover:bg-gray-50 transition-colors"
                                                >
                                                    <div className="text-sm text-gray-700">
                                                        {getDeptDisplayName(department)}
                                                        {department.track && (
                                                            <span className="text-[10px] text-gray-400 ms-1">
                                                                (
                                                                {t(
                                                                    `dream_university_track_${department.track}`,
                                                                )}
                                                                )
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-center text-sm font-medium text-gray-500">
                                                        {getDepartmentThreshold(universityId, department)}
                                                    </div>
                                                    <div className="text-end text-sm font-bold text-theme-700">
                                                        {item.studentRate}
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
                    >
                        {t("dream_university_close")}
                    </button>
                </div>
            </div>
        </div>
    );
}
